import ROUTER from '../routerMap/ROUTER.ts';
import {
  buildCreateRequestUrl,
  buildRequestCreateTabUrl,
  buildRequestDetailUrl,
  buildRequestListUrl,
  buildRequestUpdateUrl,
  isAgentFilterValue,
  isAgentValue,
  isRequestId,
  isRequestTypeKey,
  REQUEST_TYPE_BY_KEY,
  REQUEST_TYPES,
} from '../constants/Systemkey/request-type.ts';
import type {
  AgentFilterValue,
  AgentValue,
  RequestTypeKey,
  ServiceGroup,
} from '../constants/Systemkey/request-type.ts';
import type {
  ListRequestResponse,
  RequestDetailDTO,
} from '../types/api/request-management/request-management.ts';

export type RequestAction = 'detail' | 'update' | 'create-tab';

const singleParam = (params: URLSearchParams, key: string) => {
  if (params.getAll(key).length > 1)
    throw new Error(`Tham số "${key}" bị lặp.`);
  return params.get(key);
};

export const parseRequestRecordParams = (params: URLSearchParams) => {
  const id = singleParam(params, 'id');
  if (!id) throw new Error('Thiếu tham số "id".');
  if (!isRequestId(id)) throw new Error('ID yêu cầu không hợp lệ.');
  const activeTab = singleParam(params, 'activeTab');
  if (activeTab !== null && !activeTab.trim())
    throw new Error('Tab yêu cầu không hợp lệ.');
  // These are transition hints only. Never use them to select a renderer or Agent.
  singleParam(params, 'requestType');
  singleParam(params, 'agent');
  return { id: Number(id), activeTab: activeTab ?? undefined };
};

export const parseRequestListParams = (params: URLSearchParams) => {
  const requestType = singleParam(params, 'requestType');
  const agent = singleParam(params, 'agent');
  const service = singleParam(params, 'service');
  if (requestType !== null && !isRequestTypeKey(requestType))
    throw new Error('Loại yêu cầu trên đường dẫn không hợp lệ.');
  if (agent !== null && !isAgentFilterValue(agent))
    throw new Error('Agent trên đường dẫn không hợp lệ.');
  if (
    service !== null &&
    !['TESTING', 'CALIBRATION', 'INSPECTION'].includes(service)
  )
    throw new Error('Nhóm yêu cầu trên đường dẫn không hợp lệ.');
  if (requestType !== null && service !== null) {
    const config = REQUEST_TYPE_BY_KEY[requestType as RequestTypeKey];
    if (config.service !== service)
      throw new Error('Loại yêu cầu không thuộc nhóm đã chọn.');
  }
  return {
    requestType: requestType as RequestTypeKey | null,
    agent: agent as AgentFilterValue | null,
    ...(service !== null ? { service: service as ServiceGroup } : {}),
  };
};

export const resolveRequestRecord = (
  id: number,
  data: RequestDetailDTO,
  action: RequestAction,
  activeTab?: string,
) => {
  const record = data?.request;
  if (!record || record.id !== id)
    throw new Error('Không tìm thấy đúng bản ghi yêu cầu.');
  if (!isRequestTypeKey(record.requestType))
    throw new Error(
      'Bản ghi thiếu loại yêu cầu hoặc loại yêu cầu chưa được hỗ trợ.',
    );
  if (!isAgentValue(record.agent))
    throw new Error('Agent của bản ghi bị thiếu hoặc không hợp lệ.');
  if (!Array.isArray(data.listRequestData))
    throw new Error('Dữ liệu chi tiết yêu cầu không hợp lệ.');
  const config = REQUEST_TYPE_BY_KEY[record.requestType];
  if (action === 'create-tab' && config.key !== 'TEST_PQR')
    throw new Error('Chỉ yêu cầu PQR hỗ trợ tạo tab.');
  let editRecord: ListRequestResponse = record;
  if (config.key === 'TEST_PQR') {
    const tabs: ListRequestResponse[] = data.listRequestData;
    if (
      !tabs.length ||
      tabs.some(
        tab =>
          !isRequestId(tab.id) ||
          tab.requestType !== record.requestType ||
          tab.agent !== record.agent ||
          tab.jobNo !== record.jobNo ||
          !Array.isArray((tab as { listPQR?: unknown }).listPQR),
      )
    )
      throw new Error(
        'Nhóm tab PQR không hợp lệ hoặc không cùng loại/Agent/Job No.',
      );
    if (new Set(tabs.map(tab => tab.id)).size !== tabs.length)
      throw new Error('Nhóm tab PQR có ID trùng lặp.');
    if (action === 'update' || activeTab !== undefined) {
      const matches = tabs.filter(tab =>
        activeTab !== undefined ? tab.reportNo === activeTab : tab.id === id,
      );
      if (matches.length !== 1)
        throw new Error(
          'Không xác định được duy nhất tab PQR cần mở. Vui lòng kiểm tra ID và activeTab.',
        );
      editRecord = matches[0];
    }
  } else if (activeTab !== undefined) {
    throw new Error('Loại yêu cầu này không hỗ trợ activeTab.');
  }
  return { config, agent: record.agent, editRecord };
};

// Only canonical List URLs can be carried in history state as a return target.
export const getRequestListReturnTo = (
  state: unknown,
  fallback: string,
): string => {
  const target = (state as { requestListUrl?: unknown } | null)?.requestListUrl;
  if (
    typeof target !== 'string' ||
    !target.startsWith('/') ||
    target.startsWith('//')
  )
    return fallback;
  try {
    const url = new URL(target, 'https://vts.local');
    if (
      url.origin !== 'https://vts.local' ||
      url.pathname !== ROUTER.REQUEST_MANAGEMENT
    )
      return fallback;
    const { requestType, agent, service } = parseRequestListParams(url.searchParams);
    return buildRequestListUrl(
      requestType ?? undefined,
      agent ?? undefined,
      service ?? undefined,
    );
  } catch {
    return fallback;
  }
};

export const LEGACY_REQUEST_ROUTES = REQUEST_TYPES.flatMap(config => {
  const paths = config.listPaths;
  if (!paths) return [];
  const agents: AgentValue[] = paths.BH === paths.VT ? ['VT'] : ['VT', 'BH'];
  return agents
    .filter(
      agent =>
        !paths[agent].startsWith(ROUTER.REQUEST_MANAGEMENT + '/') &&
        paths[agent] !== ROUTER.REQUEST_MANAGEMENT,
    )
    .map(agent => ({
      path: paths[agent],
      requestType: config.key,
      agent,
    }));
});

export const buildLegacyRequestRedirect = (
  action: 'list' | 'create' | RequestAction,
  requestType: RequestTypeKey | undefined,
  agent: AgentValue,
  id: string | undefined,
  params: URLSearchParams,
) => {
  if (action === 'list') {
    return buildRequestListUrl(
      requestType,
      agent,
      requestType ? REQUEST_TYPE_BY_KEY[requestType].service : undefined,
    );
  }
  if (action === 'create') {
    if (!requestType) throw new Error('Thiếu loại yêu cầu.');
    return buildCreateRequestUrl(requestType, agent);
  }
  // Keep malformed/duplicate operation parameters so the entry can reject them.
  const query = new URLSearchParams();
  query.set('id', id ?? '');
  params.forEach((value, key) => {
    if (!['requestType', 'agent', 'id'].includes(key)) query.append(key, value);
  });
  const paths = {
    detail: ROUTER.REQUEST_MANAGEMENT_DETAIL,
    update: ROUTER.REQUEST_MANAGEMENT_UPDATE,
    'create-tab': ROUTER.REQUEST_MANAGEMENT_CREATE_TAB,
  };
  return `${paths[action]}?${query}`;
};

export const requestRecordUrl = (
  action: RequestAction,
  id: number,
  activeTab?: string,
) =>
  action === 'detail'
    ? buildRequestDetailUrl(id, activeTab)
    : action === 'update'
      ? buildRequestUpdateUrl(id, activeTab)
      : buildRequestCreateTabUrl(id);

export const safeLoginReturnTo = (target: unknown): string | undefined => {
  if (
    typeof target !== 'string' ||
    !target.startsWith('/') ||
    target.startsWith('//') ||
    /[\\\r\n]/.test(target)
  )
    return;
  try {
    const url = new URL(target, 'https://vts.local');
    if (url.origin === 'https://vts.local' && url.pathname !== ROUTER.DANG_NHAP)
      return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    /* Ignore invalid return targets. */
  }
};
